import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AppService } from '../app.service';
import { ApplicationInsightsService } from '../application-insights.service';
import { getHashString } from '../custom-methods';
import { HtmlReport } from '../report/report.models';

@Injectable({
  providedIn: 'root',
})
export class ContentService {
  constructor(
    private appService: AppService,
    private http: HttpClient,
    private applicationInsightsService: ApplicationInsightsService,
  ) {}

  // Get generic content from Roadrunner - pass in type and ReportCode to get an observable of that type
  getContent<T>(reportCode: string): Observable<T> {
    const httpOptions = {
      params: {
        ReportCode: reportCode,
      },
    };

    // Cache content in sessionStorage so we don't hit the API on subsequent trips
    if (sessionStorage.getItem(reportCode)) {
      return of(JSON.parse(sessionStorage.getItem(reportCode)));
    } else {
      return this.http.get<T>('/api/report/content', httpOptions).pipe(
        tap((data) => sessionStorage.setItem(reportCode, JSON.stringify(data))),
        catchError(this.appService.handleError),
      );
    }
  }

  getDashboardSummary<DashboardSummary>(mbtiType: string): Observable<DashboardSummary> {
    // Cache content in sessionStorage so we don't hit the API on subsequent trips
    if (sessionStorage.getItem('DashboardSummary')) {
      return of(JSON.parse(sessionStorage.getItem('DashboardSummary')));
    } else {
      return this.http.get<DashboardSummary>('/api/course/dashboardsummary/' + mbtiType).pipe(
        tap((data) => sessionStorage.setItem('DashboardSummary', JSON.stringify(data))),
        catchError(this.appService.handleError),
      );
    }
  }

  getReport(reportCode: string, mbtiType: string);
  getReport(reportCode: string, mbtiType: string, additionalFilters: string[]);
  getReport(reportCode: string, mbtiType: string, additionalFilters: string[], reportVariables: string);

  getReport(reportCode: string, mbtiType: string = '', additionalFilters: string[] = [], reportVariables: string = ''): Observable<HtmlReport> {
    let paramsObj = { ReportCode: reportCode };

    if (mbtiType) paramsObj['MBTIVerifiedType'] = mbtiType;

    if (additionalFilters.length > 0) paramsObj['AdditionalFilters'] = additionalFilters;

    if (reportVariables) paramsObj['ReportVariables'] = reportVariables;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: paramsObj,
    };

    // Cache report in sessionStorage so we don't hit the API on subsequent trips
    let cacheKey = reportCode + '.' + mbtiType;
    let filters = '';

    if (additionalFilters.length > 0) {
      filters += additionalFilters.join(', ');
    }
    if (reportVariables) {
      filters += reportVariables;
    }

    if (filters) {
      // hash the string because we want to keep the length of the key name from getting too large
      // hash function prtotype is defined in ./custom-methods.ts
      // cacheKey = reportCode + '.' + (mbtiType + ',' + filters).hashString();
      cacheKey = getHashString(reportCode + '.' + (mbtiType + ',' + filters)).toString();
    }

    if (sessionStorage.getItem(cacheKey)) {
      return of(JSON.parse(sessionStorage.getItem(cacheKey)));
    } else {
      return this.http.get<HtmlReport>('/api/report', httpOptions).pipe(
        tap((data) => sessionStorage.setItem(cacheKey, JSON.stringify(data))),
        catchError(this.appService.handleError),
      );
    }
  }
}
